import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { lighten, type Theme } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import RowActions from "_shared/_views/tables/row-actions";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  BulkInvitationColumnSpec,
  BulkInvitationFieldSpec,
  BulkInvitationGridColumn,
  BulkInvitationRowError,
  BulkInvitationRowValues,
  createEmptyRow,
  validateBulkInvitationRows,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { BulkInvitationAPIServiceError, BulkInvitationResultRow } from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";
import BulkInvitationResults from "legal-entities/invitations/bulk/_views/bulk-invitation-results";
import BulkInvitationRowEditor from "legal-entities/invitations/bulk/_views/bulk-invitation-row-editor";
import { RosterSheetError, downloadRosterTemplate, parseRosterWorkbook } from "legal-entities/invitations/bulk/roster-workbook";
import { useRef, useState } from "react";

/**
 * A row as the table holds it. The values are nested rather than spread alongside `rowIndex`,
 * because a row is an open map of cells and a sibling key would be one a spec could collide with.
 *
 * `problem` travels in the row rather than being looked up by the column: the table caches an
 * accessor's value until its `data` changes, so a lookup through a closure can keep showing a
 * problem somebody already fixed. Carried here, a new problem is new data.
 */
type GridRow = {
  readonly rowIndex: number;
  readonly values: BulkInvitationRowValues;
  readonly problem: string;
};

const PROBLEM_COLUMN_ID = "problem";
const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";

/** Said for a row the server refused without giving a reason, which the contract allows. */
const REFUSED_WITHOUT_A_REASON = "Attorney Hub did not send this invitation.";
const UNKNOWN_INVITATION_RESULT = "The invitation result is unknown. Review the list before trying again.";

export type BulkInvitationGridProps = {
  readonly spec: BulkInvitationColumnSpec;

  /**
   * Sends the roster and answers for every row. Rejected rows stay in the grid; invited rows are
   * cleared, so a second attempt is what is left rather than the whole sheet again.
   */
  readonly onSubmit: (rows: readonly BulkInvitationRowValues[]) => Promise<readonly BulkInvitationResultRow[]>;

  readonly submitLabel: string;

  /** Shown above the grid: what this list is for, in the caller's words. */
  readonly description?: string;
  readonly submitBlockedReason?: string;
};

const columnHelper = createMRTColumnHelper<GridRow>();

/**
 * Hoisted out of the component because it awaits inside a try, which the React Compiler will not
 * lower - a module-level function keeps the control flow exactly as written and costs the grid no
 * memoization. See app/CLAUDE.md, React Compiler.
 */
async function loadSheet(
  file: File,
  spec: BulkInvitationColumnSpec,
  setRows: (rows: readonly BulkInvitationRowValues[]) => void,
  setSheetError: (message: string | null) => void,
  setExampleRowsIgnored: (count: number) => void,
  setBusy: (busy: boolean) => void,
) {
  setBusy(true);
  setSheetError(null);
  setExampleRowsIgnored(0);
  try {
    const parsed = await parseRosterWorkbook(file, spec);
    setRows(parsed.rows);
    setExampleRowsIgnored(parsed.exampleRowsIgnored);
  } catch (error) {
    const message =
      error instanceof RosterSheetError
        ? error.userFriendlyMessage
        : "This file could not be read. Download the template and fill that in.";
    setSheetError(message);
  }
  setBusy(false);
}

async function sendRoster(
  rows: readonly BulkInvitationRowValues[],
  onSubmit: BulkInvitationGridProps["onSubmit"],
  setRows: (rows: readonly BulkInvitationRowValues[]) => void,
  setResults: (results: readonly BulkInvitationResultRow[] | null) => void,
  setRefusals: (refusals: ReadonlyMap<BulkInvitationRowValues, string>) => void,
  setSheetError: (message: string | null) => void,
  setBusy: (busy: boolean) => void,
) {
  setBusy(true);
  setSheetError(null);
  try {
    const results = await onSubmit(rows);
    setResults(results);

    // Keyed by the row itself rather than its position, because positions shift as the invited
    // rows are cleared and as somebody deletes rows afterwards. Editing a row replaces it, which
    // drops its reason - the server's answer was about what it held before.
    const refusals = new Map<BulkInvitationRowValues, string>();
    for (const result of results) {
      const row = rows[result.index];
      if (result.outcome === "rejected" && row) refusals.set(row, result.error || REFUSED_WITHOUT_A_REASON);
    }
    setRefusals(refusals);
    setRows(rows.filter((row) => refusals.has(row)));
  } catch (error) {
    const rawError = error instanceof BulkInvitationAPIServiceError ? error.error : error;
    setSheetError(getBulkInvitationSubmissionErrorMessage(readApiError(rawError)));
  }
  setBusy(false);
}

function getBulkInvitationSubmissionErrorMessage(response: ReturnType<typeof readApiError>): string {
  if (response.trusted && response.code === "vendor_rollout_unavailable") {
    return "The selected client roll-out is no longer available. Refresh the client list, choose a current roll-out, and send the roster again.";
  }
  if (response.status === 403) {
    return "Attorney Hub refused this request. You do not have permission to manage invitations for this client or use its roll-out.";
  }
  if (response.trusted && response.code === "officer_invitation_rejected") {
    return "Attorney Hub rejected this invitation request. Review the invitation details and try again.";
  }
  if (response.trusted && response.code === "client_representative_unavailable") {
    return "The selected client representative is no longer available. Choose another representative and send the list again.";
  }
  if (response.trusted && (response.category === "business" || response.category === "validation")) {
    return getApiErrorUserMessage(response) ?? "Attorney Hub refused this request. Review the invitation details and try again.";
  }
  return UNKNOWN_INVITATION_RESULT;
}

export default function BulkInvitationGrid(props: Readonly<BulkInvitationGridProps>) {
  const [rows, setRows] = useState<readonly BulkInvitationRowValues[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [results, setResults] = useState<readonly BulkInvitationResultRow[] | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [exampleRowsIgnored, setExampleRowsIgnored] = useState(0);
  const [refusals, setRefusals] = useState<ReadonlyMap<BulkInvitationRowValues, string>>(() => new Map());
  const [onlyRowsToFix, setOnlyRowsToFix] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const problems = validateBulkInvitationRows(props.spec, rows);
  const canSend = rows.length > 0 && problems.size === 0 && !busy && !props.submitBlockedReason;

  // What the grid found wrong and what the server refused, in one cell. A row refusal allows
  // another send after correction; a stale representative blocks the whole submission.
  const gridRows: GridRow[] = rows.map((values, rowIndex) => ({
    rowIndex,
    values,
    problem: describeProblems(problems.get(rowIndex), refusals.get(values)),
  }));
  const rowsToFix = gridRows.filter((row) => row.problem.length > 0);
  // Fixing the last row turns the filter off by itself, rather than leaving an empty table behind
  // a switch nobody can see any more.
  const showingOnlyRowsToFix = onlyRowsToFix && rowsToFix.length > 0;

  function replaceRows(next: readonly BulkInvitationRowValues[]) {
    setRows(next);
    // A new list makes the last run's summary describe rows nobody is looking at any more.
    setResults(null);
  }

  function addRow() {
    setEditingIndex(rows.length);
  }

  function deleteRow(index: number) {
    replaceRows(rows.filter((_row, position) => position !== index));
  }

  function saveRow(row: BulkInvitationRowValues) {
    const index = editingIndex ?? 0;
    const next = index >= rows.length ? [...rows, row] : rows.map((existing, position) => (position === index ? row : existing));
    replaceRows(next);
    setEditingIndex(null);
  }

  function pickFile() {
    fileInput.current?.click();
  }

  // Declared above the JSX that calls them. A function declaration after the component's `return`
  // is unreachable code carrying a hoisted binding, which the compiler will not lower - and the
  // whole component then renders unmemoized, silently. See app/CLAUDE.md, React Compiler.
  function replaceRowsAfterSend(next: readonly BulkInvitationRowValues[]) {
    // Deliberately not `replaceRows`: the summary the send just produced is the one thing that
    // should survive the rows changing underneath it.
    setRows(next);
  }

  function send() {
    void sendRoster(rows, props.onSubmit, replaceRowsAfterSend, setResults, setRefusals, setSheetError, setBusy);
  }

  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so picking the same file twice fires a change event the second time.
    event.target.value = "";
    if (file) void loadSheet(file, props.spec, replaceRows, setSheetError, setExampleRowsIgnored, setBusy);
  }

  const columns = [
    // First, where somebody scanning the rows starts reading - but shown only while some row has
    // a problem (see `columnVisibility`). Always defined rather than added when needed, because the
    // table fixes its column order on first render and would put a late arrival at the end.
    columnHelper.accessor((row) => row.problem, {
      id: PROBLEM_COLUMN_ID,
      header: "Problem",
      size: 180,
      Cell: ({ cell }) => (
        // error.dark rather than error.main: 5:1 against the row's tint, where main is 4.4:1.
        <Typography component="span" variant="body2" sx={{ color: "error.dark" }}>
          {cell.getValue<string>()}
        </Typography>
      ),
    }),
    ...props.spec.gridColumns.map((gridColumn) => {
      const maxLines = gridColumn.maxLines;
      return columnHelper.accessor((row) => renderGridColumnValue(row.values, gridColumn, props.spec.fields), {
        id: gridColumn.id,
        header: gridColumn.header,
        size: gridColumn.size,
        ...(maxLines ? { Cell: ({ cell }) => <ClampedText text={cell.getValue<string>()} lines={maxLines} /> } : {}),
      });
    }),
  ];

  // The Problem column first, then whose row it is, kept in view while the rest scroll sideways
  // wherever the roster is wider than its pane. Hidden columns pin harmlessly.
  const pinnedLeft = [PROBLEM_COLUMN_ID, ...props.spec.gridColumns.filter((column) => column.pinned).map((column) => column.id)];

  const tableOptions: MRT_TableOptions<GridRow> = {
    columns,
    data: showingOnlyRowsToFix ? rowsToFix : gridRows,
    // On a clean list the Problem column would be an empty column pushing the addresses aside.
    state: {
      columnVisibility: { [PROBLEM_COLUMN_ID]: rowsToFix.length > 0 },
      columnPinning: { left: pinnedLeft, right: [ROW_ACTIONS_COLUMN_ID] },
    },
    enableSorting: false,
    enableColumnFilters: false,
    // Nothing to sort, filter or rearrange, so the column menu would offer only hiding a column
    // and moving the pins - and its button takes 30px of every heading's room.
    enableColumnActions: false,
    enableColumnPinning: true,
    muiTableHeadRowProps: { sx: solidPinnedCells("th", paper) },
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    // The row is edited in a form of its own, not in place (see BulkInvitationRowEditor).
    editDisplayMode: "custom",
    enableEditing: true,
    // A pale tint and a bar down the left edge, with the text left at full strength. The row used
    // to be error.light at 0.85 opacity, which put the red message on a red row at 1.6:1.
    //
    // Opaque rather than an alpha: the table's cells inherit the row's background and paint it a
    // second time, so an 8% alpha lands as 15% and takes error.dark text under 4.5:1.
    //
    // A pinned cell paints its own layer over the row, in a `::before` beneath its content, so that
    // layer takes the tint too - and the bar, which the layer would otherwise cover.
    muiTableBodyRowProps: ({ row }) => ({
      sx: row.original.problem
        ? {
            ...solidPinnedCells("td", problemTint),
            backgroundColor: problemTint,
            "& > td:first-of-type": { boxShadow: problemBar },
            "& > td[data-pinned='true']:first-of-type::before": { boxShadow: problemBar },
          }
        : solidPinnedCells("td", paper),
    }),
    renderEmptyRowsFallback: () => (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2">
          {`No ${props.spec.noun}s yet. Add one, or upload a filled-in template.`}
        </Typography>
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <RowActions
        actions={[
          {
            key: "edit",
            label: "Edit",
            ariaLabel: `Edit row ${row.original.rowIndex + 1}`,
            icon: <EditIcon />,
            onClick: () => setEditingIndex(row.original.rowIndex),
          },
          {
            key: "delete",
            label: "Remove",
            ariaLabel: `Remove row ${row.original.rowIndex + 1}`,
            icon: <DeleteIcon />,
            destructive: true,
            onClick: () => deleteRow(row.original.rowIndex),
          },
        ]}
      />
    ),
  };

  return (
    <Stack spacing={2}>
      {props.description ? <Typography variant="body2">{props.description}</Typography> : null}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button startIcon={<AddIcon />} onClick={addRow} disabled={busy}>
          {`Add ${props.spec.noun}`}
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={() => void downloadRosterTemplate(props.spec)} disabled={busy}>
          Download template
        </Button>
        <Button startIcon={<UploadFileIcon />} onClick={pickFile} disabled={busy}>
          Upload filled-in template
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={sendDisabledReason(rows.length, problems.size)}>
          <span>
            <Button variant="contained" onClick={send} disabled={!canSend}>
              {props.submitLabel}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="Upload filled-in template"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />

      {sheetError ? <Alert severity="error">{sheetError}</Alert> : null}
      {props.submitBlockedReason ? <Alert severity="warning">{props.submitBlockedReason}</Alert> : null}
      {exampleRowsIgnored > 0 ? (
        // A status rather than the Alert's default role of alert: nothing went wrong.
        <Alert severity="info" role="status">
          {describeIgnoredExampleRows(exampleRowsIgnored)}
        </Alert>
      ) : null}
      {results ? <BulkInvitationResults results={results} noun={props.spec.noun} /> : null}

      {rowsToFix.length === 0 ? null : (
        <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ color: "error.dark", fontWeight: 500 }}>
            {describeRowsToFix(rowsToFix.length, rows.length)}
          </Typography>
          <FormControlLabel
            control={<Switch checked={onlyRowsToFix} onChange={(event) => setOnlyRowsToFix(event.target.checked)} />}
            label="Show only rows that need fixing"
          />
        </Stack>
      )}

      <MRTNoMemo options={tableOptions} />

      {editingIndex === null ? null : (
        <BulkInvitationRowEditor
          key={editingIndex}
          spec={props.spec}
          open
          title={editingIndex >= rows.length ? `Add ${props.spec.noun}` : `Edit ${props.spec.noun}`}
          row={editingIndex >= rows.length ? createEmptyRow(props.spec) : rows[editingIndex]}
          precedingRows={rows.slice(0, editingIndex)}
          onCancel={() => setEditingIndex(null)}
          onSave={saveRow}
        />
      )}
    </Stack>
  );
}

function describeIgnoredExampleRows(count: number): string {
  const rows = count === 1 ? "1 example row was" : `${count} example rows were`;
  return `${rows} left out of the upload. A row with an address ending in .invalid is an example, and nobody is invited from it.`;
}

function describeProblems(problems: readonly BulkInvitationRowError[] | undefined, refusal: string | undefined): string {
  const messages = (problems ?? []).map((problem) => problem.message);
  if (refusal) messages.push(refusal);
  return messages.join(" ");
}

function describeRowsToFix(count: number, of: number): string {
  const rows = count === 1 ? "1 row needs" : `${count} rows need`;
  return `${rows} fixing, of ${of}.`;
}

const paper = (theme: Theme) => theme.palette.background.paper;
const problemTint = (theme: Theme) => lighten(theme.palette.error.main, 0.92);
const problemBar = (theme: Theme) => `inset 4px 0 0 ${theme.palette.error.main}`;

/**
 * Draws a row's pinned cells solid, in the row's own colour. MRT gives a pinned cell
 * `opacity: 0.97` over a backdrop of its own off-white at 0.97 alpha, which leaves the columns
 * scrolling underneath legible through it and the pinned columns a shade greyer than the rest of
 * the row - the same fault the document collection table fixes for its controls column.
 *
 * Set from the row rather than the cell, because MRT's own `::before` rule outranks a cell's `sx`.
 */
function solidPinnedCells(cell: "th" | "td", paint: (theme: Theme) => string) {
  return {
    [`& > ${cell}[data-pinned='true']`]: { opacity: 1 },
    [`& > ${cell}[data-pinned='true']::before`]: { backgroundColor: paint },
  };
}

/** A grid column's fields as one line, the empty ones left out. */
function renderGridColumnValue(
  values: BulkInvitationRowValues,
  gridColumn: BulkInvitationGridColumn,
  fields: readonly BulkInvitationFieldSpec[],
): string {
  return gridColumn.keys
    .map((key) => renderCellValue(values[key], fields.find((field) => field.key === key)?.options))
    .filter((text) => text.length > 0)
    .join(gridColumn.separator ?? " ");
}

function ClampedText(props: Readonly<{ text: string; lines: number }>) {
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ display: "-webkit-box", overflow: "hidden", WebkitBoxOrient: "vertical", WebkitLineClamp: props.lines }}
    >
      {props.text}
    </Typography>
  );
}

function renderCellValue(
  value: BulkInvitationRowValues[string],
  options: readonly { value: string; label: string }[] | undefined,
): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (!value) return "";
  const option = options?.find((candidate) => candidate.value === value);
  return option ? option.label : value;
}

function sendDisabledReason(rowCount: number, problemCount: number): string {
  if (rowCount === 0) return "Add at least one row first.";
  if (problemCount > 0) {
    return problemCount === 1 ? "One row still needs fixing." : `${problemCount} rows still need fixing.`;
  }
  return "";
}
